import axios from "axios";
import server from "../server.js";

const state = {
  loading: false,
  error: {},
  experts: [],
  cabinets: [],
  agendas: [],
  ms4LicenceSearch: null,
  status: "",
  lastError: null,
};
const getters = {
  getExperts(context) {
    return context.experts;
  },
  getCabinets(context) {
    return context.cabinets;
  },
  getAgendas(context) {
    return context.agendas;
  },
  getMs4LicenceSearch(context) {
    return context.ms4LicenceSearch;
  },
  getStatus(context) {
    return context.status;
  },
  getLastError(context) {
    return context.lastError;
  },
};

const mutations = {
  UPDATE_EXPERTS(state, payload) {
    state.experts = payload.data.experts;
  },
  UPDATE_CABINETS(state, payload) {
    state.cabinets = payload.data.endUsers;
  },
  UPDATE_AGENDAS(state, payload) {
    state.agendas = payload.data.agendas;
  },
  UPDATE_MS4_LICENCE_SEARCH(state, payload) {
    state.ms4LicenceSearch = payload;
  },
  UPDATE_LOADING(state, enabled) {
    state.loading = enabled;
  },
  UPDATE_STATUS(state, status) {
    state.status = status;
  },
  UPDATE_LAST_ERROR(state, error) {
    state.lastError = error;
  },
  CLEAR_LAST_ERROR(state) {
    state.lastError = null;
  },
  UPDATE_DOCTODISPO_AUTO_RENEWAL(state, { licenceId, autoRenewalDoctoDispo }) {
    const agenda = state.agendas.find((a) => a.licenceId === licenceId);
    if (agenda) {
      agenda.autoRenewalDoctoDispo = autoRenewalDoctoDispo;
    }
  },
};

const actions = {
  async getExperts(context) {
    context.commit("UPDATE_LOADING", true);
    context.commit("UPDATE_STATUS", "");
    let res = await axios({
      method: "GET",
      url: server.url + server.urls.expert,
      headers: server.getHeader(),
    });
    context.commit("UPDATE_EXPERTS", res.data);
    context.commit("UPDATE_LOADING", false);
  },
  async getCabinets(context, param) {
    context.commit("UPDATE_LOADING", true);
    context.commit("UPDATE_STATUS", "");
    const expertCompanyId = context.state.experts?.find(
      (v) => v.name == param
    )?.companyID;
    let res = await axios({
      method: "GET",
      url:
        server.url +
        server.urls.expert +
        "/" +
        expertCompanyId +
        "/collective-end-users",
      headers: server.getHeader(),
    });
    context.commit("UPDATE_CABINETS", res.data);
    context.commit("UPDATE_LOADING", false);
  },
  async getAgendas(context) {
    context.commit("UPDATE_LOADING", true);
    context.commit("UPDATE_STATUS", "");
    let res = await axios({
      method: "GET",
      url: server.url + server.urls.agenda,
      headers: server.getHeader(),
    });
    context.commit("UPDATE_AGENDAS", res.data);
    context.commit("UPDATE_LOADING", false);
  },
  async getAgendaDetails(_context, licenceId) {
    const res = await axios({
      method: "GET",
      url: `${server.url}${server.urls.agenda}/${licenceId}/details`,
      headers: server.getHeader(),
    });
    return res.data?.data || res.data;
  },
  async searchMs4Licences(context, { n, r, limit = 50 }) {
    context.commit("UPDATE_LOADING", true);
    context.commit("CLEAR_LAST_ERROR");
    const params = new URLSearchParams();
    if (n) {
      params.set("n", n);
    }
    if (r) {
      params.set("r", r);
    }
    params.set("limit", String(limit));
    try {
      const res = await axios({
        method: "GET",
        url: `${server.url}${server.urls.agenda}/ms4-licences/search?${params.toString()}`,
        headers: server.getHeader(),
      });
      context.commit("UPDATE_MS4_LICENCE_SEARCH", res.data);
      return res.data;
    } catch (error) {
      const errorData = error.response?.data || {};
      const errorInfo = {
        type: errorData.error || "UNKNOWN_ERROR",
        message:
          errorData.details ||
          errorData.message ||
          error.message ||
          "Erreur lors de la recherche de licence",
      };
      context.commit("UPDATE_LAST_ERROR", errorInfo);
      throw errorInfo;
    } finally {
      context.commit("UPDATE_LOADING", false);
    }
  },
  async activateAgenda(context, params) {
    context.commit("UPDATE_LOADING", true);
    context.commit("UPDATE_STATUS", "");
    context.commit("CLEAR_LAST_ERROR");
    
    let data = { ...params };
    delete data.teleconsultationEnabled;
    // Use pre-built options from component if provided (includes siren), otherwise build from teleconsultationEnabled flag
    if (!data.options) {
      data.options = { teleconsultation: params.teleconsultationEnabled };
    }
    
    try {
      let res = await axios({
        method: "POST",
        url: server.url + server.urls.agenda,
        headers: server.getHeader(),
        data: data,
      });
      
      context.commit("UPDATE_STATUS", res.data.success ? "✓" : "✖︎");
      
      if (res.data.success) {
        // Refresh agendas list on success
        let refreshRes = await axios({
          method: "GET",
          url: server.url + server.urls.agenda,
          headers: server.getHeader(),
        });
        context.commit("UPDATE_AGENDAS", refreshRes.data);
      }
      
      return { success: true, data: res.data };
      
    } catch (error) {
      context.commit("UPDATE_STATUS", "✖︎");
      
      // Extract error details from response
      const errorData = error.response?.data || {};
      const errorMessage = errorData.details || errorData.message || error.message || "Une erreur inattendue s'est produite";
      const errorType = errorData.error || "UNKNOWN_ERROR";
      
      const errorInfo = {
        type: errorType,
        message: errorMessage,
        eqwoStatus: errorData.eqwoStatus,
        timestamp: new Date().toISOString(),
        raw: errorData,
      };
      
      context.commit("UPDATE_LAST_ERROR", errorInfo);
      
      // Throw to allow component to handle notification
      throw errorInfo;
      
    } finally {
      context.commit("UPDATE_LOADING", false);
    }
  },
  async updateAgenda(context, params) {
    context.commit("UPDATE_LOADING", true);
    context.commit("UPDATE_STATUS", "");
    context.commit("CLEAR_LAST_ERROR");
    
    let data = { ...params };
    // Transform options object to match API format if present
    if (data.options) {
      // options is already an object from the form
    }
    
    try {
      let res1 = await axios({
        method: "PATCH",
        url: server.url + server.urls.agenda + "/" + params.licenceId,
        headers: server.getHeader(),
        data: data,
      });
      
      context.commit("UPDATE_STATUS", res1.data.success ? "✓" : "✖︎");
      
      let res2 = await axios({
        method: "GET",
        url: server.url + server.urls.agenda,
        headers: server.getHeader(),
      });
      context.commit("UPDATE_AGENDAS", res2.data);
      
      return { success: true, data: res1.data };
      
    } catch (error) {
      context.commit("UPDATE_STATUS", "✖︎");
      
      const errorData = error.response?.data || {};
      const errorMessage = errorData.details || errorData.message || error.message || "Une erreur inattendue s'est produite";
      const errorType = errorData.error || "UNKNOWN_ERROR";
      
      const errorInfo = {
        type: errorType,
        message: errorMessage,
        eqwoStatus: errorData.eqwoStatus,
        timestamp: new Date().toISOString(),
        raw: errorData,
      };
      
      context.commit("UPDATE_LAST_ERROR", errorInfo);
      throw errorInfo;
      
    } finally {
      context.commit("UPDATE_LOADING", false);
    }
  },
  async toggleDoctoDispoAutoRenewal(
    context,
    { licenceId, autoRenewalDoctoDispo }
  ) {
    context.commit("UPDATE_LOADING", true);
    try {
      const res = await axios({
        method: "PATCH",
        url: `${server.url}/licences/doctodispo/${licenceId}/auto-renewal`,
        headers: server.getHeader(),
        data: { autoRenewalDoctoDispo },
      });

      // Recharger toutes les données depuis le serveur pour avoir l'état correct
      let res2 = await axios({
        method: "GET",
        url: server.url + server.urls.agenda,
        headers: server.getHeader(),
      });
      context.commit("UPDATE_AGENDAS", res2.data);

      if (res && res.data) {
        return res.data;
      }
    } catch (error) {
      throw new Error("Error toggling DoctoDispo auto-renewal:", error);
      throw error;
    } finally {
      context.commit("UPDATE_LOADING", false);
    }
  },
};

const store = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default store;
